/**
 * Route guards for the two sides of the app.
 *
 * - `ProtectedRoute` — any signed-in user (both sides), e.g. `/profile`.
 * - `StaffRoute` — BOI staff (staff/admin, Google SSO): the BOI cockpit.
 * - `AdminRoute` — admins only; nest it inside `StaffRoute`.
 * - `ClientRoute` — client users: the client portal. Staff and admins get in only while
 *   they preview it read-only (`usePreviewing`), under the preview banner.
 * - `NdaRoute` — inside `ClientRoute`: holds a client at `/portal/nda` until they
 *   accepted the NDA in force, then lets them on to where they were going. A preview
 *   is never held.
 * - `ContributorRoute` — the contributor area. Until contributor link sessions
 *   exist it admits only the local dev bypass, so no deployed build lets anyone in.
 * - `RoleHomeRedirect` — sends each role to its own home (`/` and `/home`).
 *
 * These only shape the UX. The backend enforces every rule again (StaffDep,
 * AdminDep, ClientDep), with the role re-read from the database per request.
 */

import { CircleNotchIcon } from "@phosphor-icons/react";
import { Navigate, Outlet, useLocation } from "react-router";

import { PreviewBanner } from "@/components/preview-banner";
import { useMe, usePortalNda, usePreviewing, type UserProfile } from "@/lib/api";
import { logger } from "@/lib/logger";
import { CLIENT_HOME, homeForRole, isSafeRelativePath, isStaffRole, STAFF_HOME } from "@/lib/roles";

export function ProtectedRoute() {
  return <RoleGate allow={() => true} />;
}

export function StaffRoute() {
  return <RoleGate allow={(me) => isStaffRole(me.role)} fallback={() => CLIENT_HOME} />;
}

export function AdminRoute() {
  return <RoleGate allow={(me) => me.role === "admin"} fallback={(me) => homeForRole(me.role)} />;
}

export function ClientRoute() {
  const { preview, isLoading } = usePreviewing();

  if (isLoading) {
    return <FullPageLoader />;
  }
  return (
    <>
      {preview && <PreviewBanner preview={preview} />}
      <RoleGate
        allow={(me) => me.role === "client" || (preview !== null && isStaffRole(me.role))}
        fallback={() => STAFF_HOME}
      />
    </>
  );
}

/** Where the NDA page sends the client once they accepted: the page they were held from. */
export interface NdaReturnState {
  from?: string;
}

export function NdaRoute() {
  const { data: nda, isLoading } = usePortalNda();
  const { preview } = usePreviewing();
  const location = useLocation();

  if (preview) {
    return <Outlet />;
  }
  if (isLoading) {
    return <FullPageLoader />;
  }
  // A failed read lets the page load: the backend gate still answers 403 `nda_required`.
  if (nda && !nda.accepted) {
    logger.debug("[nda] not accepted → /portal/nda");
    const state: NdaReturnState = { from: location.pathname + location.search };
    return <Navigate to="/portal/nda" replace state={state} />;
  }
  return <Outlet />;
}

export function ContributorRoute() {
  return <RoleGate allow={isDevBypass} />;
}

export function RoleHomeRedirect() {
  const { data: me, isLoading } = useMe();

  if (isLoading) {
    return <FullPageLoader />;
  }
  if (!me) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={homeForRole(me.role)} replace />;
}

interface RoleGateProps {
  /** Whether the signed-in user may see the nested routes. */
  allow: (me: UserProfile) => boolean;
  /** Where to send a signed-in user who may not (defaults to their own home). */
  fallback?: (me: UserProfile) => string;
}

function RoleGate({ allow, fallback }: RoleGateProps) {
  const { data: me, isLoading } = useMe();
  const location = useLocation();

  if (isLoading) {
    return <FullPageLoader />;
  }
  // Only a missing profile signs the user out: `null` once the backend refused
  // the session, `undefined` when the first load failed. A failed re-check keeps
  // the last profile, so a passing 502 doesn't bounce the user to /login.
  if (!me) {
    return <Navigate to={loginPathReturningTo(location.pathname, location.search)} replace />;
  }
  if (!allow(me)) {
    const target = fallback ? fallback(me) : homeForRole(me.role);
    logger.debug("[auth] route not allowed for role", me.role, "→", target);
    return <Navigate to={target} replace />;
  }
  return (
    <>
      {/* A new key on every navigation remounts it, and each mount re-checks the session. */}
      <RevalidateSession key={location.pathname} />
      <Outlet context={{ me }} />
    </>
  );
}

/**
 * Re-checks `/auth/me` when it mounts and when the window regains focus, so a
 * revoked or deactivated session is caught on the user's next move. The cached
 * profile stays on screen meanwhile, so there is no loader flash.
 */
function RevalidateSession() {
  useMe({ revalidateOnMount: true, revalidateOnFocus: true });
  return null;
}

/** `/login`, carrying the requested page as `?next=` when it is worth returning to. */
function loginPathReturningTo(pathname: string, search: string): string {
  const requested = pathname + search;
  if (pathname === "/" || pathname === "/home" || !isSafeRelativePath(requested)) {
    return "/login";
  }
  return `/login?next=${encodeURIComponent(requested)}`;
}

/**
 * The local dev bypass's synthetic users, which all sign in as `…@localhost`. Only a dev
 * server (`import.meta.env.DEV`) admits them; a production build never does.
 */
function isDevBypass(me: UserProfile): boolean {
  return import.meta.env.DEV && me.email.endsWith("@localhost");
}

export function FullPageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <CircleNotchIcon className="text-muted h-6 w-6 animate-spin" />
    </div>
  );
}
